import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Input, Select, Button, DataTable, ActionButtons, Badge } from '../../components/common/UIComponents';
import { BillParametersController } from '../../controllers';

export default function BillParametersPage() {
  const [records, setRecords] = useState(BillParametersController.getAll());
  const [billType, setBillType] = useState('Regular');
  const [form, setForm] = useState({ frequency: '', duration: '', interest: 'Simple', method: 'DelayMonths', rate: '', allowManual: false, rebateDays: '', billDate: '', dueDate: '', remarks: '', note1: '', note2: '', note3: '', note4: '', note5: '', note6: '' });
  const [editing, setEditing] = useState(null);

  const refresh = () => setRecords(BillParametersController.getAll());

  const handleSubmit = () => {
    const rec = { ...form, type: billType, rate: parseFloat(form.rate) || 21.00 };
    editing ? BillParametersController.update(editing, rec) : BillParametersController.add(rec);
    setForm({ frequency: '', duration: '', interest: 'Simple', method: 'DelayMonths', rate: '', allowManual: false, rebateDays: '', billDate: '', dueDate: '', remarks: '', note1: '', note2: '', note3: '', note4: '', note5: '', note6: '' });
    setEditing(null);
    refresh();
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'frequency', label: 'Bill Frequency' },
    { key: 'duration', label: 'Bill Duration' },
    { key: 'type', label: 'Bill Type', render: r => <Badge color={r.type === 'Regular' ? 'blue' : r.type === 'Opening Balance' ? 'amber' : 'purple'}>{r.type || 'Regular'}</Badge> },
    { key: 'billDate', label: 'Bill Dated' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'rate', label: 'Rate', render: r => <span className="td-mono">{r.rate}%</span> },
    { key: 'allowManual', label: 'Allow Manual', render: r => <Badge color={r.allowManual ? 'green' : 'red'}>{r.allowManual ? 'Yes' : 'No'}</Badge> },
    { key: 'method', label: 'Method' },
    { key: 'actions', label: 'Edit/Del', render: row => (
      <ActionButtons
        onEdit={() => { setForm({ frequency: row.frequency || '', duration: row.duration || '', interest: 'Simple', method: row.method || 'DelayMonths', rate: row.rate?.toString() || '', allowManual: row.allowManual || false, rebateDays: '', billDate: row.billDate || '', dueDate: row.dueDate || '', remarks: '', note1: '', note2: '', note3: '', note4: '', note5: '', note6: '' }); setBillType(row.type || 'Regular'); setEditing(row.id); }}
        onDelete={() => { BillParametersController.delete(row.id); refresh(); }}
      />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Bill Parameters" description="Configure billing frequency, interest rates and due dates" />

      <Card title="Bill Parameters Setup" style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 14 }}>
          <div className="form-label" style={{ marginBottom: 6 }}>Bill Type</div>
          <div className="radio-group">
            {['Regular', 'Supplimentary', 'Opening Reg', 'Opening Supp'].map(t => (
              <label key={t} className="radio-label">
                <input type="radio" name="billType" checked={billType === t} onChange={() => setBillType(t)} /> {t}
              </label>
            ))}
          </div>
        </div>

        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          <FormGroup label="Bill Frequency">
            <Select value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value }))}
              options={['Monthly', 'Quarterly', 'Half Yearly', 'Yearly']} placeholder="---Select---" />
          </FormGroup>
          <FormGroup label="Bill Duration">
            <Select value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))}
              options={['April-2025', 'May-2025', 'June-2025', 'July-2025', 'August-2025', 'September-2025']} placeholder="Select" />
          </FormGroup>
          <FormGroup label="Interest">
            <Select value={form.interest} onChange={e => setForm(p => ({ ...p, interest: e.target.value }))}
              options={['Simple', 'Compound']} placeholder="Simple" />
          </FormGroup>
          <FormGroup label="Method">
            <Select value={form.method} onChange={e => setForm(p => ({ ...p, method: e.target.value }))}
              options={['DelayMonths', 'DelayDays']} placeholder="DelayMonths" />
          </FormGroup>
          <FormGroup label="Rate (%)">
            <Input type="number" step="0.01" placeholder="21.00" value={form.rate} onChange={e => setForm(p => ({ ...p, rate: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Rebate Days">
            <Input placeholder="Enter Rebate Days" value={form.rebateDays} onChange={e => setForm(p => ({ ...p, rebateDays: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Bill Date" required>
            <Input type="date" value={form.billDate} onChange={e => setForm(p => ({ ...p, billDate: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Bill Due Date" required>
            <Input type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
          </FormGroup>
        </div>

        <FormGroup label="Remarks" style={{ marginBottom: 14 }}>
          <Input placeholder="Enter Remarks" value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} />
        </FormGroup>

        <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
          {[1,2,3,4,5,6].map(n => (
            <FormGroup key={n} label={`Bill Note Line ${n}`}>
              <Input placeholder={`Enter Bill Note Line ${n}`} value={form[`note${n}`]} onChange={e => setForm(p => ({ ...p, [`note${n}`]: e.target.value }))} />
            </FormGroup>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleSubmit}>{editing ? 'Update' : 'Save'}</Button>
          <Button variant="secondary" onClick={() => setForm({ frequency: '', duration: '', interest: 'Simple', method: 'DelayMonths', rate: '', allowManual: false, rebateDays: '', billDate: '', dueDate: '', remarks: '', note1: '', note2: '', note3: '', note4: '', note5: '', note6: '' })}>Clear</Button>
        </div>
      </Card>

      <DataTable title="Bill Parameters Details" columns={columns} data={records}
        onSearch={() => records} />
    </div>
  );
}
